import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { X, Upload, Video, Loader2, AlertTriangle, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Maximum video file size (50MB)
const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

// Accepted video formats
const ACCEPTED_VIDEO_FORMATS = ['video/mp4', 'video/webm'];
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.webm';

interface SiteVideoUploadProps {
  videoType: 'mp4' | 'webm';
  existingVideoUrl?: string;
  onVideoUpdated: (url: string) => void;
  label?: string;
  description?: string;
  showWarning?: boolean;
  warningMessage?: string;
}

export function SiteVideoUpload({
  videoType,
  existingVideoUrl,
  onVideoUpdated,
  label,
  description,
  showWarning = false,
  warningMessage,
}: SiteVideoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();

  const displayLabel = label || `Hero Video (${videoType.toUpperCase()})`;
  const acceptedMimeType = videoType === 'mp4' ? 'video/mp4' : 'video/webm';
  const acceptedExtension = videoType === 'mp4' ? '.mp4' : '.webm';

  const uploadVideo = useCallback(async (file: File) => {
    // Validate file type
    if (!ACCEPTED_VIDEO_FORMATS.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: `Please upload a ${videoType.toUpperCase()} video file`,
        variant: 'destructive',
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      toast({
        title: 'File too large',
        description: `Maximum video size is ${MAX_VIDEO_SIZE_MB}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB`,
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      // Check authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Generate unique filename
      const timestamp = Date.now();
      const extension = videoType;
      const fileName = `hero-video-${timestamp}.${extension}`;
      const filePath = `site-images/hero/videos/${fileName}`;

      console.log('Uploading video:', { fileName, filePath, size: file.size, type: file.type });

      // Upload directly to Supabase storage (no optimization needed for video)
      const { data, error: uploadError } = await supabase.storage
        .from('unit-images')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true,
          cacheControl: '31536000', // 1 year cache
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('unit-images')
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL for uploaded video');
      }

      console.log('Video uploaded successfully:', urlData.publicUrl);
      setProgress(100);

      // Update parent component
      onVideoUpdated(urlData.publicUrl);

      toast({
        title: 'Success',
        description: `${videoType.toUpperCase()} video uploaded successfully`,
      });
    } catch (error: unknown) {
      console.error('Video upload error:', error);
      let errorMessage = 'Failed to upload video';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive',
        duration: 10000,
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [videoType, onVideoUpdated, toast]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadVideo(file);
    }
    // Reset the input
    e.target.value = '';
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) {
      setIsDragging(true);
    }
  }, [uploading]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploading) {
      return;
    }

    const file = e.dataTransfer.files[0];
    if (file) {
      await uploadVideo(file);
    }
  }, [uploading, uploadVideo]);

  const removeVideo = () => {
    onVideoUpdated('');
    toast({
      title: 'Video removed',
      description: 'Video removed successfully',
    });
  };

  const inputId = `site-video-upload-${videoType}`;

  return (
    <div className="space-y-4">
      {showWarning && warningMessage && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{warningMessage}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <Label>{displayLabel}</Label>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </>
          )}
        </Button>
      </div>

      <Input
        id={inputId}
        type="file"
        accept={acceptedExtension}
        onChange={handleFileUpload}
        className="hidden"
      />

      {uploading && (
        <div className="space-y-2">
          <Progress value={progress} />
          <p className="text-sm text-muted-foreground text-center">
            Uploading video... {Math.round(progress)}%
          </p>
        </div>
      )}

      {/* Video Preview */}
      {existingVideoUrl && (
        <div className="relative group">
          <video
            src={existingVideoUrl}
            className="w-full h-48 object-cover rounded-lg border border-border bg-black"
            controls
            muted
          >
            Your browser does not support the video tag.
          </video>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={removeVideo}
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Play className="h-3 w-3" />
            {videoType.toUpperCase()} Video
          </div>
        </div>
      )}

      {/* Drop Zone */}
      {!existingVideoUrl && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors
            ${isDragging 
              ? 'border-primary bg-primary/10' 
              : 'border-border bg-muted/20'
            }
            ${!uploading ? 'cursor-pointer hover:border-primary/50' : ''}
          `}
          onClick={() => {
            if (!uploading) {
              document.getElementById(inputId)?.click();
            }
          }}
        >
          <Video className={`h-12 w-12 mb-2 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
          <p className={`text-sm transition-colors ${isDragging ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            {isDragging 
              ? `Drop ${videoType.toUpperCase()} video here` 
              : `Drag and drop ${videoType.toUpperCase()} video here, or click to browse`}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Max file size: {MAX_VIDEO_SIZE_MB}MB
          </p>
        </div>
      )}
    </div>
  );
}
